import {
  AddressInput,
  AddressTypeInput,
  AddressValidationResultTypenames,
  SuggestedAddress,
} from '@varo/graphql-schema-types';
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';
import Snackbar, {
  SNACKBAR_TIMEOUT,
} from 'shared/components/vxl/Snackbar/Snackbar';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';

interface AgreementInfo {
  url: string;
  title: string;
}

interface IdenticalAddressData {
  originalAddress: AddressInput;
  suggestedAddress: AddressInput;
  message?: string;
}

type AddressVerificationData = SuggestedAddress | IdenticalAddressData;

type AddressPayload = {
  address: AddressInput;
  addressType: AddressTypeInput;
};

export type MyVaroContextProps = {
  yearSelectionScreenTitle?: string;
  monthSelectionScreenTitle?: string;
  selectedMonthScreenTitle?: string;
  setSelectedMonthScreenTitle?: (selectedMonthScreenTitle: string) => void;
  setYearSelectionScreenTitle?: (yearSelectionScreenTitle: string) => void;
  setMonthSelectionScreenTitle?: (monthSelectionScreenTitle: string) => void;
  taxYearSelectionScreenTitle?: string;
  setTaxYearSelectionScreenTitle?: (
    taxYearSelectionScreenTitle: string,
  ) => void;
  updatedEmailId?: string;
  setUpdatedEmailId?: (email: string) => void;
  password?: string;
  setPassword?: (password: string) => void;
  lastFourPhoneDigits?: string;
  setLastFourPhoneDigits?: (num: string) => void;
  addressVerification?:
    | {
        fullAddress?: AddressVerificationData;
        addressType?: AddressValidationResultTypenames;
        isVloc?: boolean;
        isLimitedAccess: boolean;
        fromConfirmAddress?: boolean;
        screenType?: string;
      }
    | undefined;
  setAddressVerification?: Dispatch<
    SetStateAction<
      | {
          fullAddress?: AddressVerificationData;
          addressType?: AddressValidationResultTypenames;
          isVloc?: boolean;
          isLimitedAccess: boolean;
          fromConfirmAddress?: boolean;
          screenType?: string;
        }
      | undefined
    >
  >;
  setSnackBarMessage?: React.Dispatch<React.SetStateAction<string>>;
  selectedAgreement?: AgreementInfo;
  setSelectedAgreement?: (info: AgreementInfo) => void;
  accountStatementPDFId?: string;
  setAccountStatementPDFId?: (accountStatementPDFId: string) => void;
  accountStatementPDFTitle?: string;
  setAccountStatementPDFTitle?: (accountStatementPDFTitle: string) => void;
  taxStatementPDFId?: string;
  setTaxStatementPDFId?: (taxStatementPDFId: string) => void;
  taxStatementPDFTitle?: string;
  setTaxStatementPDFTitle?: (taxStatementPDFTitle: string) => void;
  updatedAddressList?: AddressPayload[];
  setUpdatedAddressList?: React.Dispatch<
    React.SetStateAction<AddressPayload[]>
  >;
  isCheckedHomeAndMailingAddress?: boolean;
  setIsCheckedHomeAndMailingAddress?: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >;
  isIdenticalHomeAndMailingAddress?: boolean;
  setIsIdenticalHomeAndMailingAddress?: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >;
  recentlyBuiltAddressList?: AddressPayload[];
  setRecentlyBuiltAddressList?: React.Dispatch<
    React.SetStateAction<AddressPayload[] | undefined>
  >;
};

export const MyVaroContext = React.createContext<MyVaroContextProps>({});

const MyVaroContextProvider = ({ children }: PropsWithChildren) => {
  const [yearSelectionScreenTitle, setYearSelectionScreenTitle] = useState<
    string | undefined
  >(undefined);
  const [accountStatementPDFId, setAccountStatementPDFId] =
    useState<string>('');
  const [accountStatementPDFTitle, setAccountStatementPDFTitle] =
    useState<string>('');
  const [taxStatementPDFId, setTaxStatementPDFId] = useState<string>('');
  const [taxStatementPDFTitle, setTaxStatementPDFTitle] = useState<string>('');
  const [monthSelectionScreenTitle, setMonthSelectionScreenTitle] = useState<
    string | undefined
  >(undefined);
  const [selectedMonthScreenTitle, setSelectedMonthScreenTitle] = useState<
    string | undefined
  >(undefined);
  const [taxYearSelectionScreenTitle, setTaxYearSelectionScreenTitle] =
    useState<string | undefined>(undefined);
  const [updatedEmailId, setUpdatedEmailId] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [lastFourPhoneDigits, setLastFourPhoneDigits] = useState<string>('');
  const [selectedAgreement, setSelectedAgreement] = useState<AgreementInfo>({
    url: '',
    title: '',
  });

  const [addressVerification, setAddressVerification] = useState<{
    fullAddress?: AddressVerificationData;
    addressType?: AddressValidationResultTypenames;
    isVloc?: boolean;
    isLimitedAccess: boolean;
    fromConfirmAddress?: boolean;
    screenType?: string;
  }>();

  const [updatedAddressList, setUpdatedAddressList] = useState<
    AddressPayload[]
  >([]);
  const [isCheckedHomeAndMailingAddress, setIsCheckedHomeAndMailingAddress] =
    useState<boolean | undefined>(undefined);
  const [
    isIdenticalHomeAndMailingAddress,
    setIsIdenticalHomeAndMailingAddress,
  ] = useState<boolean | undefined>(undefined);
  const [recentlyBuiltAddressList, setRecentlyBuiltAddressList] = useState<
    AddressPayload[] | undefined
  >(undefined);

  const [snackBarMessage, setSnackBarMessage] = useState<string>('');

  useAutoHideSnackbar(
    Boolean(snackBarMessage),
    () => {
      setSnackBarMessage('');
    },
    SNACKBAR_TIMEOUT,
  );

  return (
    <MyVaroContext.Provider
      value={{
        selectedMonthScreenTitle,
        setSelectedMonthScreenTitle,
        monthSelectionScreenTitle,
        yearSelectionScreenTitle,
        setMonthSelectionScreenTitle,
        setYearSelectionScreenTitle,
        taxYearSelectionScreenTitle,
        setTaxYearSelectionScreenTitle,
        updatedEmailId,
        setUpdatedEmailId,
        password,
        setPassword,
        addressVerification,
        setAddressVerification,
        lastFourPhoneDigits,
        setLastFourPhoneDigits,
        setSnackBarMessage,
        selectedAgreement,
        setSelectedAgreement,
        accountStatementPDFId,
        setAccountStatementPDFId,
        accountStatementPDFTitle,
        setAccountStatementPDFTitle,
        taxStatementPDFId,
        setTaxStatementPDFId,
        taxStatementPDFTitle,
        setTaxStatementPDFTitle,
        updatedAddressList,
        setUpdatedAddressList,
        isCheckedHomeAndMailingAddress,
        setIsCheckedHomeAndMailingAddress,
        isIdenticalHomeAndMailingAddress,
        setIsIdenticalHomeAndMailingAddress,
        recentlyBuiltAddressList,
        setRecentlyBuiltAddressList,
      }}
    >
      {children}
      {snackBarMessage ? (
        <View style={styles.snackBarWrapper}>
          <Snackbar text={snackBarMessage} duration={SNACKBAR_TIMEOUT} />
        </View>
      ) : null}
    </MyVaroContext.Provider>
  );
};

export default MyVaroContextProvider;

const styles = StyleSheet.create({
  snackBarWrapper: {
    alignSelf: 'center',
    maxWidth: 800,
    width: '100%',
  },
});
